export type TaxBreakdownLine = {
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
  amount: number;
};

export function normalizeTaxBreakdown(value: unknown): TaxBreakdownLine[] {
  if (!value) return [];

  const parsed = typeof value === 'string' ? safeParseJson(value) : value;
  if (!Array.isArray(parsed)) return [];

  return (parsed as unknown[])
    .map((entry) => {
      const tax = (entry ?? {}) as { name?: unknown; rate?: unknown; type?: unknown; amount?: unknown };
      return {
        name: String(tax.name || 'Tax'),
        rate: Number(tax.rate || 0),
        type: (tax.type === 'INCLUSIVE' ? 'INCLUSIVE' : 'EXCLUSIVE') as TaxBreakdownLine['type'],
        amount: Number(tax.amount || 0),
      };
    })
    .filter((tax) => tax.amount > 0 || tax.rate > 0);
}

export function aggregateTaxBreakdown(values: unknown[]): TaxBreakdownLine[] {
  const totals = new Map<string, TaxBreakdownLine>();

  for (const value of values) {
    for (const tax of normalizeTaxBreakdown(value)) {
      const key = `${tax.name}|${tax.rate}|${tax.type}`;
      const existing = totals.get(key);
      if (existing) {
        existing.amount += tax.amount;
      } else {
        totals.set(key, { ...tax });
      }
    }
  }

  return [...totals.values()].map((tax) => ({
    ...tax,
    amount: Math.round(tax.amount * 100) / 100,
  }));
}

export function formatTaxLabel(tax: TaxBreakdownLine) {
  const typeLabel = tax.type === 'INCLUSIVE' ? 'included' : 'extra';
  return `${tax.name.includes('%') ? tax.name : `${tax.name} ${tax.rate}%`} (${typeLabel})`;
}

function safeParseJson(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

/** POS-I112: route of the Tax Rates page (Administration > Master Data > Tax Rates). */
export const TAX_RATES_PATH = '/admin/master-data/tax-rates';
